import { BellOff } from "lucide-react";
import { Button, EmptyState, SeverityBadge, Spinner } from "@/components/ds";
import { Timestamp } from "./Timestamp";
import { cn } from "@/lib/cn";

/** One notification, as GET /notifications returns it. */
export interface NotificationItem {
  id: number;
  title: string;
  message?: string;
  severity?: string;
  link?: string;
  read?: boolean;
  created_at: string;
}

export interface NotificationsPanelProps {
  items: NotificationItem[];
  unreadCount: number;
  loading?: boolean;
  /** A row was chosen: mark it read and follow its link. */
  onOpen: (item: NotificationItem) => void;
  onMarkAllRead?: () => void;
  markingAll?: boolean;
  className?: string;
}

/**
 * The panel the top bar's bell opens (AppHeader `notificationsPanel`). DLP
 * addition: the dashboard used to show an unread count with nothing behind
 * it. Each row is one button (title, message, time and severity) that marks
 * the notification read and opens its link; "Mark all read" clears the badge.
 * Presentational: src/app/HeaderNotifications.tsx owns the data.
 */
export function NotificationsPanel({
  items, unreadCount, loading, onOpen, onMarkAllRead, markingAll, className,
}: NotificationsPanelProps) {
  return (
    <div className={cn("flex w-[360px] max-w-[calc(100vw-32px)] flex-col", className)}>
      <div className="flex items-center justify-between gap-3 border-b border-[var(--gridline)] px-4 py-3">
        <div className="flex items-baseline gap-2">
          <span className="text-body font-medium text-[var(--d2d-text-primary)]">Notifications</span>
          {unreadCount > 0 ? (
            <span className="text-dense tabular-nums text-[var(--d2d-text-muted)]">{unreadCount} unread</span>
          ) : null}
        </div>
        {onMarkAllRead && unreadCount > 0 ? (
          <Button variant="ghost" size="sm" onClick={onMarkAllRead} loading={markingAll} disabled={markingAll}>
            Mark all read
          </Button>
        ) : null}
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 px-4 py-8 text-dense text-[var(--d2d-text-muted)]">
          <Spinner size={16} /> Loading notifications
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          className="py-8"
          headingLevel={3}
          icon={<BellOff />}
          title="You're all caught up"
          description="New alerts and system messages appear here."
        />
      ) : (
        // The list holds read and unread alike (eval F29: it was labelled
        // "Unread notifications").
        <ul className="max-h-[400px] overflow-y-auto py-1" aria-label="Recent notifications">
          {items.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => onOpen(n)}
                className="flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-[var(--ds-color-bg-hover)] focus-visible:bg-[var(--ds-color-bg-hover)] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--ds-color-ring)]"
              >
                <span className="flex items-start justify-between gap-3">
                  <span className="text-dense font-medium text-[var(--d2d-text-primary)]">{n.title}</span>
                  {n.severity ? <SeverityBadge value={n.severity} className="shrink-0">{n.severity}</SeverityBadge> : null}
                </span>
                {n.message ? (
                  <span className="line-clamp-2 text-dense text-[var(--d2d-text-secondary)]">{n.message}</span>
                ) : null}
                <Timestamp value={n.created_at} format="relative" className="text-micro text-[var(--d2d-text-muted)]" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
